/**
 * Module for handling modal elements' state
 */

import delegateListener from './delegate-listener';

class ModalHelper {

  /**
   * @param {object} [options] -
   * @param {string} [options.modals] - Selector for modal element
   * @param {string} [options.openers] - Selector for buttons that open a modal
   * @param {string} [options.closers] - Selector for buttons that close the modal they're within
   * @param {string} [options.openClass] - Classname for modal element, signifying it's 'open' state
   */
  constructor(options) {
    const defaults = {
      modals: '[data-modal]',
      openers: '[data-modal-target]',
      closers: '[data-modal-close]',
      openClass: 'modal--open',
      delegateListener,
    };

    Object.assign(this, defaults, options);

    // Prepare Esc handler
    this.handleEscPress = this._handleEscPress.bind(this);
  }

  /**
   * Initializes the instance by selecting elements and adding listeners to buttons
   */
  init() {
    document.body.addEventListener('click', this.delegateListener(
      this.closers, this._handleCloserClick.bind(this)
    ));

    document.body.addEventListener('click', this.delegateListener(
      this.openers, this._handleOpenerClick.bind(this)
    ));

    if(document.querySelector('.' + this.openClass)) {
      // Lock scrolling initially, if a modal was open on page load
      this.lockScrolling();
      this.bindEsc();
    }
  }

  /**
   * Opens a given modal element
   * @param {HTMLElement} modalEl - The modal element that should be opened
   */
  openModal(modalEl) {
    modalEl.classList.add(this.openClass);
    this.lockScrolling();

    this.bindEsc();
  }

  // Prevent scrolling main document behind modal
  lockScrolling() {
    document.body.style.overflow = 'hidden';
  }

  /**
   * Closes a given modal element
   * @param {HTMLElement} modalEl - The modal element that should be closed
   */
  closeModal(modalEl) {
    modalEl.classList.remove(this.openClass);

    // Unprevent scrolling
    document.body.style.overflow = '';

    this.unbindEsc();
  }

  _handleOpenerClick(event) {
    const eventTarget = event.delegateTarget || event.currentTarget;
    const target = document.querySelector(
      `[data-modal="${eventTarget.dataset.modalTarget}"]`
    );

    if(target) {
      this.openModal(target);
    }
  }

  _handleCloserClick(event) {
    // TODO: Check IE compatibility for .closest(), it might require polyfilling
    const eventTarget = event.delegateTarget || event.currentTarget;
    const target = eventTarget.closest(this.modals);

    if(target) {
      this.closeModal(target);
    } else { /* No target - no op. */}
  }

  _handleEscPress(event) {
    switch (event.key) {
      case 'Escape': {
        const currentModal = document.querySelector('.' + this.openClass);
        if(!currentModal) return;
        this.closeModal(currentModal);
        break;
      }
    }
  }

  bindEsc() {
    window.addEventListener('keydown', this.handleEscPress);
  }

  unbindEsc() {
    window.removeEventListener('keydown', this.handleEscPress);
  }
}

module.exports = ModalHelper;
